import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Single source of truth for auth state. Subscribes to onAuthStateChange ONCE
 * for the whole app; every `useAuth()` consumer reads from this context
 * instead of creating its own listener.
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const finishLoading = (nextSession: Session | null) => {
      if (!mounted) return;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);
    };

    // Mobile browsers can occasionally leave auth storage/network reads hanging
    // while restoring a tab or resuming a PWA. Do not block the app shell forever.
    const fallbackTimer = window.setTimeout(() => {
      finishLoading(null);
    }, 5000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'TOKEN_REFRESHED' && !session) {
          finishLoading(null);
        } else if (event === 'SIGNED_OUT') {
          finishLoading(null);
        } else {
          finishLoading(session);
        }
      }
    );

    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        window.clearTimeout(fallbackTimer);
        if (error) {
          console.log('Session retrieval error, clearing local state:', error.message);
          finishLoading(null);
        } else {
          finishLoading(session);
        }
      })
      .catch((error) => {
        window.clearTimeout(fallbackTimer);
        console.log('Session retrieval failed, continuing signed out:', error?.message ?? error);
        finishLoading(null);
      });

    return () => {
      mounted = false;
      window.clearTimeout(fallbackTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (ctx) return ctx;
  // Fallback for any consumer that mounts outside the provider (e.g. tests).
  // Returns a stable no-op shape so the app never crashes.
  return {
    user: null,
    session: null,
    loading: false,
    signOut: async () => { await supabase.auth.signOut(); },
  };
};
